import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Spotify PKCE (Authorization Code + PKCE) login — no client secret needed,
 * so REACT_APP_SPOTIFY_CLIENT_ID can live in the frontend. Tokens are kept
 * in localStorage and auto-refreshed. Redirect URI must match what's
 * registered in the Spotify Developer Dashboard.
 *
 * Scopes requested:
 *   streaming user-read-email user-read-private user-modify-playback-state
 *   user-read-playback-state
 *
 * IMPORTANT: Spotify Web Playback SDK requires a Premium account for full
 * tracks. Free accounts will see a "player_initialization_error" with code
 * "account".
 */
const AUTH_URL = "https://accounts.spotify.com/authorize";
const TOKEN_URL = "https://accounts.spotify.com/api/token";
const SCOPES = [
  "streaming",
  "user-read-email",
  "user-read-private",
  "user-modify-playback-state",
  "user-read-playback-state",
].join(" ");

const STORAGE_KEY = "tapin_spotify_token";
const VERIFIER_KEY = "tapin_spotify_pkce";
const CID_CACHE_KEY = "tapin_spotify_cid";

const ENV_CLIENT_ID = process.env.REACT_APP_SPOTIFY_CLIENT_ID || "";
const REDIRECT_URI = typeof window !== "undefined" ? `${window.location.origin}/spotify/callback` : "";

// The effective Client ID: env var takes priority for local dev, else pulled
// from the admin-configured Mongo row via /api/integrations/spotify/public.
// We cache the last-known value in localStorage so login() doesn't block on a
// network roundtrip after the first successful fetch.
let _cidPromise = null;
const fetchClientId = async () => {
  if (ENV_CLIENT_ID) return ENV_CLIENT_ID;
  if (_cidPromise) return _cidPromise;
  _cidPromise = (async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${base}/api/integrations/spotify/public`);
      if (!res.ok) return localStorage.getItem(CID_CACHE_KEY) || "";
      const data = await res.json();
      const cid = data?.client_id || "";
      if (cid) localStorage.setItem(CID_CACHE_KEY, cid);
      else localStorage.removeItem(CID_CACHE_KEY);
      return cid;
    } catch {
      return localStorage.getItem(CID_CACHE_KEY) || "";
    }
  })();
  return _cidPromise;
};

// --- PKCE helpers --------------------------------------------------------
const randomString = (len = 64) => {
  const arr = new Uint8Array(len);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, (b) => ("0" + (b & 0xff).toString(16)).slice(-2)).join("");
};
const sha256 = async (plain) => {
  const data = new TextEncoder().encode(plain);
  const buf = await window.crypto.subtle.digest("SHA-256", data);
  return buf;
};
const b64url = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)))
  .replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");

const loadToken = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); } catch { return null; }
};
const saveToken = (t) => {
  if (!t) { localStorage.removeItem(STORAGE_KEY); return; }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
};

export default function useSpotifyAuth() {
  const [token, setToken] = useState(loadToken);
  const [refreshing, setRefreshing] = useState(false);
  const [clientId, setClientId] = useState(ENV_CLIENT_ID || localStorage.getItem(CID_CACHE_KEY) || "");
  const refreshTimer = useRef(null);

  // Resolve Client ID on mount (env fast-path, otherwise backend-config call)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cid = await fetchClientId();
      if (!cancelled) setClientId(cid);
    })();
    return () => { cancelled = true; };
  }, []);

  const configured = !!clientId;

  // Schedule a refresh ~5 min before expiry
  const scheduleRefresh = useCallback((tok) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    if (!tok?.expires_at || !tok?.refresh_token) return;
    const ms = Math.max(5000, tok.expires_at - Date.now() - 5 * 60 * 1000);
    refreshTimer.current = setTimeout(() => { refresh().catch(() => {}); }, ms);
  }, []); // eslint-disable-line

  const refresh = useCallback(async () => {
    const tok = loadToken();
    if (!tok?.refresh_token) return null;
    const cid = clientId || (await fetchClientId());
    if (!cid) return null;
    setRefreshing(true);
    try {
      const body = new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: tok.refresh_token,
        client_id: cid,
      });
      const res = await fetch(TOKEN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      if (!res.ok) throw new Error(`refresh ${res.status}`);
      const data = await res.json();
      const next = {
        access_token: data.access_token,
        refresh_token: data.refresh_token || tok.refresh_token,
        expires_at: Date.now() + (data.expires_in || 3600) * 1000,
        scope: data.scope,
      };
      saveToken(next); setToken(next); scheduleRefresh(next);
      return next;
    } catch (e) {
      // Refresh failed — force user to sign in again
      saveToken(null); setToken(null);
      return null;
    } finally {
      setRefreshing(false);
    }
  }, [scheduleRefresh]);

  // Kick off refresh timer whenever token changes
  useEffect(() => {
    if (token) scheduleRefresh(token);
    return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); };
  }, [token, scheduleRefresh]);

  const login = useCallback(async () => {
    const cid = clientId || (await fetchClientId());
    if (!cid) {
      throw new Error("Spotify integration isn't configured yet. Ask an admin to set the Client ID in Admin → Integrations.");
    }
    const verifier = randomString(64);
    const challenge = b64url(await sha256(verifier));
    localStorage.setItem(VERIFIER_KEY, verifier);
    const params = new URLSearchParams({
      client_id: cid,
      response_type: "code",
      redirect_uri: REDIRECT_URI,
      code_challenge_method: "S256",
      code_challenge: challenge,
      scope: SCOPES,
    });
    // Remember where the user was so we can bounce back after callback
    localStorage.setItem("tapin_spotify_return", window.location.pathname + window.location.search);
    window.location.href = `${AUTH_URL}?${params.toString()}`;
  }, [clientId]);

  const exchangeCode = useCallback(async (code) => {
    const verifier = localStorage.getItem(VERIFIER_KEY);
    localStorage.removeItem(VERIFIER_KEY);
    const cid = clientId || (await fetchClientId());
    if (!verifier || !cid) return null;
    const body = new URLSearchParams({
      grant_type: "authorization_code",
      code, redirect_uri: REDIRECT_URI, client_id: cid, code_verifier: verifier,
    });
    const res = await fetch(TOKEN_URL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
    if (!res.ok) throw new Error(`token exchange ${res.status}`);
    const data = await res.json();
    const next = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: Date.now() + (data.expires_in || 3600) * 1000,
      scope: data.scope,
    };
    saveToken(next); setToken(next); scheduleRefresh(next);
    return next;
  }, [scheduleRefresh, clientId]);

  const logout = useCallback(() => {
    saveToken(null); setToken(null);
  }, []);

  // Auto-refresh if we loaded an already-expired token
  useEffect(() => {
    const tok = loadToken();
    if (tok && tok.expires_at && tok.expires_at < Date.now() + 10_000) {
      refresh();
    }
  }, [refresh]);

  const getFreshAccessToken = useCallback(async () => {
    const tok = loadToken();
    if (!tok) return null;
    if (tok.expires_at && tok.expires_at < Date.now() + 10_000) {
      const next = await refresh();
      return next?.access_token || null;
    }
    return tok.access_token;
  }, [refresh]);

  return {
    configured,
    clientId,
    token,
    loggedIn: !!token?.access_token,
    refreshing,
    login, logout, exchangeCode, refresh, getFreshAccessToken,
  };
}
